import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { Compass, MapPin, Bed, Search, Trees, CalendarRange, ChefHat } from 'lucide-react';
import CarRentalBanner from './components/CarRentalBanner';
import Footer from './components/Footer';
import MapView from './components/MapView';
import { EXPANSION_ZONES } from './lib/expansionZones';
import { FAQ } from './lib/faq';
import { SCOPE } from './lib/brandScope';
import { ACCOMMODATIONS, TASTING_STOPS } from './lib/data';
import { REGION_HUBS, STATE_HUBS, getStateHref } from './lib/trailHubs';
import { INTENT_PAGES } from './lib/intentPages';
import { bookingSearchUrl } from './utils/booking';
import { setPageSeo } from './utils/seo';

const STAGE_NAV = [
  { href: '#choose-place', label: 'Choose a Place' },
  { href: '#pick-season', label: 'Pick a Season' },
  { href: '#itinerary', label: 'Build Itinerary' },
  { href: '#route-stops', label: 'Route Stops' },
  { href: '#where-to-stay', label: 'Where to Stay' },
  { href: '#faq', label: 'FAQ' },
] as const;

export default function App() {
  const chapter = STATE_HUBS[0];

  useEffect(() => {
    setPageSeo({
      title: 'Oregon Truffle Travel Guide | Portland, Willamette Valley, Eugene, and Corvallis',
      description:
        'Plan Oregon truffle weekends around Portland, Willamette Valley, Eugene, and Corvallis, with verified outings, boutique stays, and winter-versus-spring trip guidance.',
      path: '/',
        jsonLd: {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Oregon Truffle Travel Guide',
          url: 'https://trufflehuntingoregon.com/',
          description:
            'An Oregon truffle guide built around outings, dining, places to stay, and seasonal trip planning.',
          about: ['Oregon truffles', 'Portland', 'Willamette Valley', 'Eugene', 'Corvallis', 'truffle dog training', 'truffle travel'],
        },
    });
  }, []);

  return (
    <div className="min-h-screen bg-bg text-ink">
      <header className="border-b border-border bg-white/85 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent text-white flex items-center justify-center">
              <Compass size={18} />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-lg">Truffle Hunting Oregon</div>
              <div className="text-[10px] uppercase tracking-widest text-ink-light">Oregon travel guide</div>
            </div>
          </Link>
          <Link to={getStateHref(chapter.state)} className="btn-minimal">
            Open Oregon guide
          </Link>
        </div>
        <div className="border-t border-border/70 bg-bg/95">
          <nav className="max-w-screen-2xl mx-auto px-6 md:px-12 py-3 flex flex-wrap gap-2">
            {STAGE_NAV.map((item) => (
              <a key={item.href} href={item.href} className="pill-minimal">
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12 md:py-16 space-y-12">
        <section id="choose-place" className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-10 items-start scroll-mt-56 md:scroll-mt-32">
          <div className="space-y-6">
            <span className="pill-minimal active">{SCOPE.label}</span>
            <h1 className="text-5xl md:text-7xl font-serif tracking-tight leading-[1.02]">
              Plan an <span className="italic font-light text-accent">Oregon truffle weekend</span> around the state’s best truffle towns and forest routes.
            </h1>
            <p className="text-lg text-ink-light max-w-3xl leading-relaxed">
              {SCOPE.heroTagline}
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="pill-minimal active">Portland</span>
              <span className="pill-minimal active">Newberg</span>
              <span className="pill-minimal active">McMinnville</span>
              <span className="pill-minimal active">Dayton</span>
              <span className="pill-minimal active">Eugene</span>
              <span className="pill-minimal">Estacada foothills</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              <article className="card-minimal p-5 bg-white/80">
                <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Featured stops</p>
                <p className="text-4xl font-serif text-accent mt-2">{TASTING_STOPS.length}</p>
              </article>
              <article className="card-minimal p-5 bg-white/80">
                <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Stay bases</p>
                <p className="text-4xl font-serif text-accent mt-2">{ACCOMMODATIONS.length}</p>
              </article>
              <article className="card-minimal p-5 bg-white/80">
                <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Route zones</p>
                <p className="text-4xl font-serif text-accent mt-2">{REGION_HUBS.length}</p>
              </article>
            </div>
            <div className="rounded-[28px] border border-border bg-[linear-gradient(135deg,rgba(41,67,42,0.06),rgba(255,255,255,0.92))] p-6 space-y-3">
              <p className="text-[10px] uppercase tracking-widest font-bold text-accent">A Practical Oregon Guide</p>
              <p className="text-base text-ink-light leading-relaxed">
                This guide brings together Portland day trips, Willamette Valley weekends, Eugene festival travel, and Corvallis truffle culture without losing the feel of a real trip.
              </p>
            </div>
          </div>

          <div className="rounded-[32px] overflow-hidden border border-border bg-sidebar/50 h-[420px] sm:h-[480px] md:h-[520px] relative">
            <MapView
              stops={TASTING_STOPS}
              accommodations={ACCOMMODATIONS}
              fitBounds
            />
            <div className="absolute left-4 bottom-4 z-[500] rounded-[18px] border border-border bg-white/92 px-4 py-3 shadow-sm">
              <div className="flex flex-wrap gap-3 text-[11px] text-ink-light">
                <span className="inline-flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-accent border border-ink/30" />
                  Route stops
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-ink border border-ink/30" />
                  Hotels
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-6">
          <div className="max-w-4xl space-y-3">
            <span className="pill-minimal active">Start here</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">Choose the right Oregon truffle planning guide.</h2>
            <p className="text-lg text-ink-light leading-relaxed">
              The homepage is now a hub for the main trip questions: festival travel, guided hunting, seasonal timing, and broad regions without exact wild-foraging locations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {INTENT_PAGES.map((page) => (
              <Link key={page.slug} to={`/${page.slug}/`} className="card-minimal p-7 bg-white/80 space-y-4 block hover:border-accent transition-colors">
                <span className="pill-minimal active">{page.eyebrow}</span>
                <h3 className="text-3xl font-serif tracking-tight">{page.title}</h3>
                <p className="text-sm text-ink-light leading-relaxed">{page.description}</p>
              </Link>
            ))}
          </div>
        </section>
        <section id="pick-season" className="grid grid-cols-1 md:grid-cols-3 gap-6 scroll-mt-56 md:scroll-mt-32">
          <article className="card-minimal p-7 bg-white/80 space-y-3">
            <CalendarRange className="text-accent" size={22} />
            <h2 className="text-3xl font-serif tracking-tight">Two real seasons</h2>
            <p className="text-sm text-ink-light leading-relaxed">
              Winter is the main season for public events, guided outings, and easy trip planning. Spring adds a quieter second season with a smaller set of options.
            </p>
          </article>
          <article className="card-minimal p-7 bg-white/80 space-y-3">
            <Trees className="text-accent" size={22} />
            <h2 className="text-3xl font-serif tracking-tight">A small, useful map</h2>
            <p className="text-sm text-ink-light leading-relaxed">
              Portland works for city-base outings, Willamette Valley for the classic weekend, Eugene for festival and dog-training travel, and Corvallis for the academic side of Oregon truffles.
            </p>
          </article>
          <article className="card-minimal p-7 bg-white/80 space-y-3">
            <ChefHat className="text-accent" size={22} />
            <h2 className="text-3xl font-serif tracking-tight">The meal matters</h2>
            <p className="text-sm text-ink-light leading-relaxed">
              Dining matters here because the trip is usually planned around more than the hunt alone.
            </p>
          </article>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-[1fr_0.9fr] gap-8">
          <article className="card-minimal p-8 bg-white/80 space-y-5">
            <span className="pill-minimal active">Why this route</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">How the Oregon trip comes together.</h2>
            <div className="space-y-4 text-lg text-ink-light leading-relaxed">
              <p>Portland is the easiest place to base a day trip into the foothills if you want a guided outing without shifting hotels.</p>
              <p>Willamette Valley is still the classic Oregon truffle weekend, with the strongest mix of stays, dining, and forest access.</p>
              <p>Eugene works best for travelers planning around festival dates, dog events, and a more event-driven stay.</p>
              <p>Corvallis adds the research and society side of the story, with talks, gatherings, and a campus-adjacent base.</p>
            </div>
          </article>

          <article className="card-minimal p-8 bg-sidebar/70 space-y-5">
            <span className="pill-minimal">Trip Shape</span>
            <h2 className="text-4xl font-serif tracking-tight">Keep the weekend simple.</h2>
            <div className="grid gap-4">
              <div className="rounded-[22px] bg-white/80 border border-border p-5 text-sm text-ink-light leading-relaxed">
                Pick one main outing or event for the day instead of stacking too many stops.
              </div>
              <div className="rounded-[22px] bg-white/80 border border-border p-5 text-sm text-ink-light leading-relaxed">
                Pair the forest side of the trip with one dinner reservation and one stay base.
              </div>
              <div className="rounded-[22px] bg-white/80 border border-border p-5 text-sm text-ink-light leading-relaxed">
                Use Portland, Eugene, or Corvallis when you want a city base; use the valley when you want the fuller weekend route.
              </div>
            </div>
          </article>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-8 items-start">
          <article className="card-minimal p-8 bg-white/80 space-y-5">
            <span className="pill-minimal active">Season frame</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">Winter is easier. Spring is narrower.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-[24px] bg-[linear-gradient(180deg,rgba(41,67,42,0.08),rgba(255,255,255,0.92))] border border-border p-5 space-y-3">
                <p className="text-[10px] uppercase tracking-widest font-bold text-accent">Winter season</p>
                <p className="font-serif text-3xl tracking-tight">The easy first trip</p>
                <p className="text-sm text-ink-light leading-relaxed">
                  Tours are easier to identify, events are public, and the seasonal calendar is easier to read.
                </p>
              </div>
              <div className="rounded-[24px] bg-[linear-gradient(180deg,rgba(138,98,54,0.10),rgba(255,255,255,0.92))] border border-border p-5 space-y-3">
                <p className="text-[10px] uppercase tracking-widest font-bold text-accent">Spring White shoulder season</p>
                <p className="font-serif text-3xl tracking-tight">The narrower planning window</p>
                <p className="text-sm text-ink-light leading-relaxed">
                  Spring trips can still be rewarding, especially for private outings and training weekends that run into late spring.
                </p>
              </div>
            </div>
          </article>

          <article className="card-minimal p-8 bg-sidebar/70 space-y-5">
            <span className="pill-minimal">The towns</span>
            <h2 className="text-4xl font-serif tracking-tight">Where each part of the trip fits best.</h2>
            <div className="grid gap-4">
              <div className="rounded-[22px] bg-white/80 border border-border p-5">
                <p className="text-[10px] uppercase tracking-widest font-bold text-accent">Portland / Estacada</p>
                <p className="mt-2 text-sm text-ink-light leading-relaxed">Useful for city-base departures, PSU market Saturdays, and foothills day trips.</p>
              </div>
              <div className="rounded-[22px] bg-white/80 border border-border p-5">
                <p className="text-[10px] uppercase tracking-widest font-bold text-accent">Newberg</p>
                <p className="mt-2 text-sm text-ink-light leading-relaxed">Useful for tours, resort stays, and Newberg-area dining.</p>
              </div>
              <div className="rounded-[22px] bg-white/80 border border-border p-5">
                <p className="text-[10px] uppercase tracking-widest font-bold text-accent">McMinnville</p>
                <p className="mt-2 text-sm text-ink-light leading-relaxed">Useful as a town base with walkable lodging, dining, and event access.</p>
              </div>
              <div className="rounded-[22px] bg-white/80 border border-border p-5">
                <p className="text-[10px] uppercase tracking-widest font-bold text-accent">Dayton</p>
                <p className="mt-2 text-sm text-ink-light leading-relaxed">Useful as a dining stop within a short Willamette Valley itinerary.</p>
              </div>
              <div className="rounded-[22px] bg-white/80 border border-border p-5">
                <p className="text-[10px] uppercase tracking-widest font-bold text-accent">Eugene</p>
                <p className="mt-2 text-sm text-ink-light leading-relaxed">Useful for festival events, dog-training weekends, and a city-based southern Oregon leg.</p>
              </div>
              <div className="rounded-[22px] bg-white/80 border border-border p-5">
                <p className="text-[10px] uppercase tracking-widest font-bold text-accent">Corvallis</p>
                <p className="mt-2 text-sm text-ink-light leading-relaxed">Useful for public truffle talks, annual society events, and campus-adjacent academic stays.</p>
              </div>
            </div>
          </article>
        </section>

        <section id="itinerary" className="grid grid-cols-1 xl:grid-cols-[1fr_1fr] gap-8 items-start scroll-mt-56 md:scroll-mt-32">
          <article className="card-minimal p-8 bg-white/80 space-y-5">
            <span className="pill-minimal active">Winter trip planning</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">Start here for the clearest first trip.</h2>
            <div className="space-y-4 text-lg text-ink-light leading-relaxed">
              <p>Winter is the clearest first trip because the tours and festival programming are easier to verify in advance.</p>
              <p>A practical winter itinerary usually centers on one main activity, one dinner reservation, and one overnight stay.</p>
            </div>
            <div className="grid gap-3">
              <div className="rounded-[22px] bg-sidebar/60 border border-border p-4 text-sm text-ink-light">Best for: first-time visitors and travelers who want the most documented season.</div>
              <div className="rounded-[22px] bg-sidebar/60 border border-border p-4 text-sm text-ink-light">Best shape: one hunt or event, one dinner reservation, and one overnight stay.</div>
            </div>
          </article>

          <article className="card-minimal p-8 bg-sidebar/70 space-y-5">
            <span className="pill-minimal">Spring White planning</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">A smaller season with a different rhythm.</h2>
            <div className="space-y-4 text-lg text-ink-light leading-relaxed">
              <p>Spring trips are quieter, greener, and better suited to travelers who are comfortable planning around smaller calendars and private bookings.</p>
              <p>It works best as a slower follow-up to the winter season rather than a replacement for the main festival months.</p>
            </div>
            <div className="grid gap-3">
              <div className="rounded-[22px] bg-white/80 border border-border p-4 text-sm text-ink-light">Best for: travelers who are comfortable planning around private, by-appointment options.</div>
              <div className="rounded-[22px] bg-white/80 border border-border p-4 text-sm text-ink-light">Best shape: one private outing or training weekend, one good meal, and a quieter pace than winter festival travel.</div>
            </div>
          </article>
        </section>

        <section className="space-y-6">
          <div className="max-w-4xl space-y-3">
            <span className="pill-minimal active">How truffle trips work</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">Two visual guides for readers new to truffle travel.</h2>
            <p className="text-lg text-ink-light leading-relaxed">
              These infographics help explain both the basic truffle lifecycle and the hunt-to-table process before readers start comparing routes, operators, and seasonal windows.
            </p>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <figure className="card-minimal bg-white/80 p-4 md:p-6">
              <img
                src="/truffle-basics-infographic.png"
                alt="Infographic explaining what truffles are, where they thrive, and how quality and season affect them."
                className="w-full rounded-[24px] border border-border"
                loading="lazy"
              />
              <figcaption className="pt-4 text-sm text-ink-light leading-relaxed">
                A quick reference for truffle basics, seasonal rarity, and the soil-and-tree conditions that shape growth.
              </figcaption>
            </figure>
            <figure className="card-minimal bg-white/80 p-4 md:p-6">
              <img
                src="/truffle-hunting-infographic.png"
                alt="Infographic explaining how truffles grow, how trained dogs locate them, and how the hunt-to-table process works."
                className="w-full rounded-[24px] border border-border"
                loading="lazy"
              />
              <figcaption className="pt-4 text-sm text-ink-light leading-relaxed">
                A companion reference showing the hunt-to-table process, from locating truffles in the forest to handling them for the dining side of the trip.
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="space-y-6">
          <div className="max-w-4xl space-y-3">
            <span className="pill-minimal active">Next in Oregon</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">Current Oregon route patterns.</h2>
            <p className="text-lg text-ink-light leading-relaxed">
              These are the four parts of Oregon that make the trip most interesting right now.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {EXPANSION_ZONES.map((zone) => (
              <article key={zone.slug} className="card-minimal p-7 bg-white/80 space-y-4">
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-accent italic">{zone.shortLabel}</p>
                  <h3 className="text-3xl font-serif tracking-tight">{zone.title}</h3>
                  <span className="pill-minimal">{zone.status}</span>
                </div>
                <p className="text-sm text-ink-light leading-relaxed">{zone.summary}</p>
                <p className="text-sm text-ink-light leading-relaxed">
                  <span className="font-semibold text-ink">Best for:</span> {zone.travelerFit}
                </p>
                <p className="text-sm text-ink-light leading-relaxed">
                  <span className="font-semibold text-ink">What to expect:</span> {zone.evidence}
                </p>
                {zone.stayCandidates.length ? (
                  <p className="text-sm text-ink-light leading-relaxed">
                    <span className="font-semibold text-ink">Stay candidates:</span> {zone.stayCandidates.join(', ')}
                  </p>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        <section id="route-stops" className="space-y-6 scroll-mt-56 md:scroll-mt-32">
          <div className="max-w-4xl space-y-3">
            <span className="pill-minimal active">Featured now</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">
              Route stops, <span className="italic font-light text-accent">kept to a short list.</span>
            </h2>
            <p className="text-lg text-ink-light leading-relaxed">
              These are the stops that shape the trip across Portland, the valley, Eugene, and Corvallis.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TASTING_STOPS.map((stop) => (
              <article key={stop.id} className="card-minimal p-7 bg-white/80 space-y-4">
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-accent italic">{stop.city}</p>
                  <h3 className="text-3xl font-serif tracking-tight">{stop.name}</h3>
                  <p className="text-sm text-ink-light leading-relaxed">{stop.description}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {stop.highlights?.map((item) => (
                    <span key={item} className="pill-minimal">{item}</span>
                  ))}
                </div>
                <div className="space-y-2 text-sm text-ink-light leading-relaxed">
                  <p><span className="font-semibold text-ink">Season:</span> {stop.season}</p>
                  <p><span className="font-semibold text-ink">Access:</span> {stop.hours}</p>
                </div>
                {stop.routeNotes?.length ? (
                  <div className="space-y-2 text-sm text-ink-light leading-relaxed">
                    {stop.routeNotes.map((note) => (
                      <p key={note}>{note}</p>
                    ))}
                  </div>
                ) : null}
                {stop.verificationNote ? (
                  <div className="rounded-[18px] bg-sidebar/60 border border-border p-4 text-sm text-ink-light leading-relaxed">
                    <span className="font-semibold text-ink">Know before you go:</span> {stop.verificationNote}
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        <section id="where-to-stay" className="space-y-6 scroll-mt-56 md:scroll-mt-32">
          <div className="max-w-4xl space-y-3">
            <span className="pill-minimal active">Stay bases</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">
              Places to stay within the route.
            </h2>
            <p className="text-lg text-ink-light leading-relaxed">
              The stay list stays short so each property still feels tied to a real trip shape.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ACCOMMODATIONS.map((stay) => (
              <article key={stay.id} className="card-minimal p-7 bg-white/80 space-y-4">
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-accent italic">{stay.city} / {stay.style}</p>
                  <h3 className="text-3xl font-serif tracking-tight">{stay.name}</h3>
                  <p className="text-sm text-ink-light leading-relaxed">{stay.description}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {stay.amenities.map((item) => (
                    <span key={item} className="pill-minimal">{item}</span>
                  ))}
                </div>
                {stay.routeNotes?.length ? (
                  <div className="space-y-2 text-sm text-ink-light leading-relaxed">
                    {stay.routeNotes.map((note) => (
                      <p key={note}>{note}</p>
                    ))}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-3 pt-2">
                  <a
                    href={bookingSearchUrl(stay)}
                    target="_blank"
                    rel="noreferrer noopener sponsored"
                    data-stay22="book"
                    className="btn-minimal bg-accent text-white border-accent hover:bg-ink hover:border-ink"
                  >
                    Check rates
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <CarRentalBanner />

        <section id="faq" className="space-y-5 scroll-mt-56 md:scroll-mt-32">
          <span className="pill-minimal active">FAQ</span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FAQ.map((item) => (
              <details key={item.question} className="card-minimal bg-white/80 p-6">
                <summary className="font-serif text-2xl cursor-pointer">{item.question}</summary>
                <p className="mt-4 text-sm text-ink-light leading-relaxed">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

